import React, { useState } from "react";
import { useForm, useFieldArray } from "react-hook-form";
import api from "../../api/api";
import {
  Box,
  Button,
  Grid,
  MenuItem,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import Alerts from "../Alerts";
import AddIcon from "@mui/icons-material/Add";

const Edit = ({ selectedBanner, setOption }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [warning, setWarning] = useState({});
  const {
    register,
    control,
    handleSubmit,
    watch,
    formState: { errors },
    setValue,
  } = useForm({
    defaultValues: selectedBanner, // Assegura que os valores iniciais sejam carregados
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: "carousel",
  });

  const type = watch("type");
  const standardImgUrl = watch("imgUrl");
  const standardImgUrlMobile = watch("imgUrlMobile");
  const videoUrl = watch("videoUrl");

  // Função para extrair o ID do Vimeo da URL ou retornar o próprio valor se já for um ID
  const getVimeoVideoId = (url) => {
    if (!url) return null;

    // Se for apenas um número, assume que é o ID do Vimeo
    if (/^\d+$/.test(url.trim())) {
      return url.trim();
    }

    // Tenta extrair o ID de diferentes formatos de URL do Vimeo
    const vimeoRegex = /(?:vimeo\.com\/|player\.vimeo\.com\/video\/)(\d+)/;
    const match = url.match(vimeoRegex);
    return match ? match[1] : null;
  };

  const vimeoVideoId = getVimeoVideoId(videoUrl);

  const onSubmit = (data) => {
    // Se o tipo for CAROUSEL, ajusta href, imgUrl, imgUrlMobile e videoUrl para ""
    if (data.type === "CAROUSEL") {
      data.href = "";
      data.imgUrl = "";
      data.imgUrlMobile = "";
      data.videoUrl = "";
    }
    if (data.type !== "CAROUSEL") {
      data.carousel = [];
    }
    api
      .post(`/banners`, data)
      .then((response) => {
        console.log(response.data);
        handleOpenSnackbar();
        setWarning({
          type: "success",
          message: "Banner atualizado com sucesso",
        });
        setOption("view"); // Retorna para a visualização após a atualização
      })
      .catch((error) => {
        console.error("Erro na atualização", error);
        handleOpenSnackbar();
        setWarning({
          type: "error",
          message: "Erro ao atualizar banner",
        });
      });
  };

  const handleOpenSnackbar = () => {
    setOpenSnackbar(true);
  };
  const handleCloseSnackbar = () => {
    setOpenSnackbar(false);
  };
  return (
    <Box p={2}>
      <Button
        onClick={() => setOption("view")}
        variant="outlined"
        color="primary"
        sx={{ mb: 2 }}
      >
        <ArrowBackIcon />
        Voltar
      </Button>

      <form onSubmit={handleSubmit(onSubmit)}>
        <Box maxWidth="7xl" mx="auto" mt={4}>
          <Grid container spacing={3}>
            <Grid xs={12} mb={3}>
              <TextField
                select
                fullWidth
                label="Tipo"
                value={watch("type") || ""}
                {...register("type", { required: "Este campo é obrigatório" })}
                error={!!errors.type}
                helperText={errors.type?.message}
              >
                <MenuItem value="STANDARD">STANDARD</MenuItem>
                <MenuItem value="CAROUSEL">CAROUSEL</MenuItem>
              </TextField>
            </Grid>

            {type === "CAROUSEL" &&
              fields.map((field, index) => {
                const imgUrl = watch(`carousel.${index}.imgUrl`);
                const imgUrlMobile = watch(`carousel.${index}.imgUrlMobile`);
                const carouselVimeoId = getVimeoVideoId(imgUrl);
                return (
                  <Grid
                    item
                    xs={12}
                    key={field.id}
                    border={1}
                    borderColor="grey.300"
                    borderRadius={2}
                    p={2}
                    bgcolor="grey.50"
                    mb={2}
                  >
                    <Box display="flex" justifyContent="space-between" mb={2}>
                      <Typography variant="subtitle2" gutterBottom mb={2}>
                        Banner {index + 1}
                      </Typography>
                      <Button
                        onClick={() => remove(index)}
                        variant="outlined"
                        color="error"
                        size="small"
                        startIcon={<CloseIcon />}
                      >
                        Remover
                      </Button>
                    </Box>

                    <Grid container spacing={2}>
                      <Grid item xs={12} md={12}>
                        <TextField
                          fullWidth
                          size="small"
                          label="URL de redirecionamento"
                          {...register(`carousel.${index}.href`)}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          label="URL da imagem desktop ou ID do Vimeo"
                          {...register(`carousel.${index}.imgUrl`)}
                          error={!!errors.imgUrl}
                          helperText={
                            errors.imgUrl?.message ||
                            "Insira uma URL de imagem ou ID do Vimeo"
                          }
                          sx={{ pb: 2 }}
                        />
                        {imgUrl?.trim() && carouselVimeoId && (
                          <Grid item xs={12} md={6} mt={2}>
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              mb={1}
                            >
                              Preview do Vídeo Vimeo:
                            </Typography>
                            <Box
                              sx={{
                                position: "relative",
                                width: "100%",
                                maxWidth: 640,
                                paddingBottom: "56.25%", // 16:9 aspect ratio
                                height: 0,
                                borderRadius: 2,
                                overflow: "hidden",
                                boxShadow: 2,
                                border: "1px solid #ccc",
                              }}
                            >
                              <Box
                                component="iframe"
                                src={`https://player.vimeo.com/video/${carouselVimeoId}`}
                                title={`Preview vídeo Vimeo ${index + 1}`}
                                sx={{
                                  position: "absolute",
                                  top: 0,
                                  left: 0,
                                  width: "100%",
                                  height: "100%",
                                  border: 0,
                                }}
                                allow="autoplay; fullscreen; picture-in-picture"
                                allowFullScreen
                              />
                            </Box>
                          </Grid>
                        )}
                        {imgUrl?.trim() && !carouselVimeoId && (
                          <Grid item xs={12} md={6}>
                            <Box
                              component="img"
                              src={imgUrl}
                              alt={`Preview ${index + 1}`}
                              sx={{
                                width: "100%",
                                maxWidth: 180,
                                height: 100,
                                objectFit: "cover",
                                borderRadius: 1,
                                boxShadow: 1,
                                border: "1px solid #ccc",
                              }}
                            />
                          </Grid>
                        )}
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          label="URL da imagem mobile (800x839)"
                          {...register(`carousel.${index}.imgUrlMobile`)}
                          error={!!errors.imgUrlMobile}
                          helperText={errors.imgUrlMobile?.message}
                          sx={{ pb: 2 }}
                        />
                        {imgUrlMobile && imgUrlMobile.trim() && (
                          <Grid item xs={12} md={6}>
                            <Box
                              component="img"
                              src={imgUrlMobile.trim()}
                              alt={`Preview ${index + 1}`}
                              sx={{
                                width: "100%",
                                maxWidth: 180,
                                height: 100,
                                objectFit: "cover",
                                borderRadius: 1,
                                boxShadow: 1,
                                border: "1px solid #ccc",
                              }}
                            />
                          </Grid>
                        )}
                      </Grid>
                    </Grid>
                  </Grid>
                );
              })}

            {type === "CAROUSEL" && (
              <Grid item xs={12} mb={4}>
                <Button
                  type="button"
                  onClick={() => append({ href: "", imgUrl: "" })}
                  variant="contained"
                  startIcon={<AddIcon />}
                >
                  Adicionar mais
                </Button>
              </Grid>
            )}

            {type !== "CAROUSEL" && (
              <>
                <Grid xs={12} mb={2}>
                  <TextField
                    select
                    fullWidth
                    value={watch("position") || ""}
                    label="Posição"
                    {...register("position", {
                      required: "Este campo é obrigatório",
                    })}
                    error={!!errors.position}
                    helperText={errors.position?.message}
                  >
                    <MenuItem value="TOP">TOP</MenuItem>
                    <MenuItem value="MID">MID</MenuItem>
                    <MenuItem value="BOTTOM">BOTTOM</MenuItem>
                  </TextField>
                </Grid>
                <Grid xs={12} mb={2}>
                  <TextField fullWidth label="Href" {...register("href")} />
                </Grid>
                <Grid container>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="URL da Imagem Desktop"
                      {...register("imgUrl")}
                      sx={{ pb: 2, width: { xs: "100%", md: "98%" } }}
                    />
                    {standardImgUrl?.trim() && (
                      <Grid xs={12} md={6}>
                        <Box
                          component="img"
                          src={standardImgUrl}
                          alt="Preview Standard"
                          sx={{
                            width: "100%",
                            maxWidth: 180,
                            height: 100,
                            objectFit: "cover",
                            borderRadius: 1,
                          }}
                        />
                      </Grid>
                    )}
                  </Grid>

                  <Grid xs={12} md={6} sx={{ mt: { xs: 4, md: 0 } }}>
                    <TextField
                      fullWidth
                      label="URL da Imagem Mobile"
                      {...register("imgUrlMobile")}
                      sx={{ pb: 2, width: { xs: "100%", md: "100%" } }}
                    />
                    {standardImgUrlMobile?.trim() && (
                      <Grid item xs={12} md={6}>
                        <Box
                          component="img"
                          src={standardImgUrlMobile}
                          alt="Preview Standard"
                          sx={{
                            width: "100%",
                            maxWidth: 180,
                            height: 100,
                            objectFit: "cover",
                            borderRadius: 1,
                            boxShadow: 1,
                            border: "1px solid #ccc",
                          }}
                        />
                      </Grid>
                    )}
                  </Grid>
                </Grid>

                <Grid xs={12} my={3}>
                  <TextField
                    fullWidth
                    label="ID do vídeo do Vimeo"
                    {...register("videoUrl")}
                    helperText="Insira o ID do vídeo do Vimeo"
                    sx={{ pb: vimeoVideoId ? 2 : 0 }}
                  />
                  {videoUrl?.trim() && vimeoVideoId && (
                    <Grid item xs={12} mt={2}>
                      <Typography variant="body2" color="text.secondary" mb={1}>
                        Preview do Vídeo Vimeo:
                      </Typography>
                      <Box
                        sx={{
                          position: "relative",
                          width: "100%",
                          maxWidth: 640,
                          paddingBottom: "56.25%", // 16:9 aspect ratio
                          height: 0,
                          borderRadius: 2,
                          overflow: "hidden",
                          boxShadow: 2,
                          border: "1px solid #ccc",
                        }}
                      >
                        <Box
                          component="iframe"
                          src={`https://player.vimeo.com/video/${vimeoVideoId}`}
                          title="Preview vídeo Vimeo"
                          sx={{
                            position: "absolute",
                            top: 0,
                            left: 0,
                            width: "100%",
                            height: "100%",
                            border: 0,
                          }}
                          allow="autoplay; fullscreen; picture-in-picture"
                          allowFullScreen
                        />
                      </Box>
                    </Grid>
                  )}
                </Grid>
              </>
            )}

            <Grid xs={12} mb={2}>
              <TextField
                fullWidth
                label="Título"
                {...register("title", { required: "Este campo é obrigatório" })}
                error={!!errors.title}
                helperText={errors.title?.message}
              />
            </Grid>

            <Grid xs={12} mb={4}>
              <TextField
                fullWidth
                label="Descrição"
                {...register("text", { required: "Este campo é obrigatório" })}
                error={!!errors.text}
                helperText={errors.text?.message}
              />
            </Grid>

            <Grid xs={12}>
              <Button type="submit" variant="contained" color="primary">
                Salvar Alterações
              </Button>
            </Grid>
          </Grid>
        </Box>
      </form>

      <Alerts
        open={openSnackbar}
        handleClose={handleCloseSnackbar}
        type={warning.type}
        message={warning.message}
      />
    </Box>
  );
};

export default Edit;
